/**
 * MAESTRO API Service
 * Handles all communication with the backend
 *
 * P1-5: Updated for cookie-based auth with CSRF protection
 * - Uses credentials: 'include' to send cookies
 * - Reads CSRF token from cookie and sends in header
 * - Falls back to header-based auth for compatibility
 *
 * GOVERNANCE: constitution_cyberSec.json Section 5 - Zero cleartext
 * API_BASE imported from centralized config with HTTPS enforcement
 */

import { API_BASE } from '../config/api.config.js';

// Re-export API_BASE for backwards compatibility
export { API_BASE };

// CSRF token cookie name

/**
 * Render FastAPI's array-shaped 422 `detail` as readable text.
 *
 * A string detail passes through unchanged (legacy HTTPException). An array of
 * {loc, msg} becomes "field: message", naming the offending field -- an error the user
 * cannot act on is barely better than no error at all.
 */
export function flattenErrorDetail(detail) {
  if (!detail) return null;
  if (typeof detail === 'string') return detail;
  if (!Array.isArray(detail)) return typeof detail.msg === 'string' ? detail.msg : null;
  const parts = detail
    .map((d) => {
      if (typeof d === 'string') return d;
      if (!d || typeof d.msg !== 'string') return null;
      // loc is like ["body","items",0,"product_id"]; the last string segment is the
      // field the user can actually do something about.
      const field = Array.isArray(d.loc) ? [...d.loc].reverse().find((x) => typeof x === 'string') : null;
      return field && field !== 'body' ? `${field}: ${d.msg}` : d.msg;
    })
    .filter(Boolean);
  return parts.length ? parts.join('; ') : null;
}

const CSRF_COOKIE_NAME = 'mdm_csrf_token';

// Token storage key for cross-origin auth fallback
const TOKEN_STORAGE_KEY = 'mdm_access_token';

/**
 * Get CSRF token from cookie
 */
function getCsrfToken() {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Token storage helpers for cross-origin deployments
 * When frontend and backend are on different domains, cookies don't work.
 * Fall back to localStorage + Authorization header.
 */
export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token) {
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable (private browsing, etc.)
  }
}

export function clearStoredToken() {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Ignore errors
  }
}

/**
 * Assemble the headers every API request carries: JSON content type, the stored
 * bearer token when cookies cannot cross origins, and the CSRF token on mutations.
 *
 * V-EXPORT-FE: extracted from fetchAPI so fetchAPIBlob shares ONE request builder
 * instead of re-deriving auth/CSRF by hand (importApi.js exportBatchCsv duplicated
 * it, and duplicated builders drift).
 */
function buildRequestHeaders(endpoint, method, extraHeaders) {
  const headers = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };

  // Cross-origin auth: Add Authorization header if token is stored and not already provided
  // This enables auth when cookies can't be shared across domains
  if (!headers.Authorization) {
    const storedToken = getStoredToken();
    if (storedToken) {
      headers.Authorization = `Bearer ${storedToken}`;
    }
  }

  // P1-5: Add CSRF token for mutations (non-GET requests)
  // NASTY-006: Log warning if CSRF token is missing for mutations
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    } else if (import.meta.env.PROD && !headers.Authorization) {
      // In production without Authorization header, missing CSRF token is a concern
      console.warn(
        '[API] CSRF token missing for mutation request to:', endpoint,
        '- Request may be rejected. Try refreshing the page.'
      );
    }
  }

  return headers;
}

/**
 * IMPL-AUTH-RESILIENCE: Silent refresh on 401.
 *
 * Resolves true when the session was refreshed and the original request should be
 * retried. Resolves false after clearing the stored token and announcing
 * `auth:expired` -- the caller then surfaces the original 401 as an error.
 */
async function attemptSilentRefresh() {
  try {
    const refreshResponse = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (refreshResponse.ok) {
      const refreshData = await refreshResponse.json();
      // Update stored token if provided (cross-origin fallback)
      if (refreshData?.access_token) {
        setStoredToken(refreshData.access_token);
      }
      return true;
    }
  } catch (refreshError) {
    // Refresh failed, fall through to clear and dispatch
    if (import.meta.env.DEV) console.warn('[API] Silent refresh failed:', refreshError);
  }

  // Refresh failed - clear tokens and notify
  clearStoredToken();
  window.dispatchEvent(new CustomEvent('auth:expired'));
  return false;
}

/**
 * Turn a non-OK response into the Error every caller expects: message normalized
 * across backend error shapes, HTTP status attached.
 */
async function responseToError(response) {
  const error = await response.json().catch(() => ({}));
  // Normalize across backend error shapes: custom envelope {message, details},
  // legacy FastAPI HTTPException {detail}, and validation {details: {errors: [{msg}]}}.
  //
  // ⛔ FastAPI's NATIVE 422 PUTS AN ARRAY IN `detail` (singular) -- [{loc, msg, type}].
  // The chain reached `error.detail`, found a truthy array, and handed it to
  // new Error(), which stringifies an array of objects to the literal text
  // "[object Object]". Verified by execution, not inferred. The
  // `details?.errors?.[0]?.msg` branch is a CUSTOM envelope shape (details, PLURAL)
  // and was never reached for a real validation failure.
  //
  // This matters most on the charge path: a cart item failing the checkout contract
  // 422s at create-payment-intent, and the buyer's Pay button read "[object Object]"
  // with nothing naming the field.
  const err = new Error(
    error.message ||
      flattenErrorDetail(error.detail) ||
      error.details?.errors?.[0]?.msg ||
      `API Error: ${response.status}`
  );
  // Attach HTTP status so callers (e.g., BR-GAP-008 429 anti-enum handler) can branch.
  err.status = response.status;
  // The app envelope's machine-readable context ({details: {code: 'expired'|...}}) rides
  // along so callers branch on the served code, not on message wording.
  if (error.details && typeof error.details === 'object') err.details = error.details;
  return err;
}

/**
 * Generic fetch wrapper with error handling
 *
 * P1-5: Updated to:
 * - Include credentials (cookies) in all requests
 * - Add CSRF token header for mutations
 * - Support both cookie and header auth
 *
 * IMPL-20260106-OCR-GRADER-PHASE3: Exported for use by gradingApi.js
 */
export async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const method = options.method || 'GET';

  if (import.meta.env.DEV) {
    console.debug(`[API] Request: ${method} ${url}`, options.body ? JSON.parse(options.body) : '');
  }

  const headers = buildRequestHeaders(endpoint, method, options.headers);

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      // P1-5: Include cookies in all requests
      credentials: 'include',
    });

    if (!response.ok) {
      // Retry original request with _retry flag to prevent infinite loop
      if (response.status === 401 && !options._retry && (await attemptSilentRefresh())) {
        return fetchAPI(endpoint, { ...options, _retry: true });
      }
      throw await responseToError(response);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      if (import.meta.env.DEV) console.debug(`[API] Response: 204 No Content (${endpoint})`);
      return null;
    }

    const data = await response.json();
    if (import.meta.env.DEV) {
      console.debug(`[API] Response: 200 OK (${endpoint})`, data);
    }
    return data;
  } catch (error) {
    // LOW-001: Gate console.error behind DEV mode
    if (import.meta.env.DEV) console.error(`API call failed: ${endpoint}`, error);
    throw error;
  }
}

/**
 * Multipart (FormData) sibling of fetchAPI — the SAME auth posture, minus the JSON Content-Type.
 *
 * ⛔ WHY THIS EXISTS. Every vault upload (CLZ/CSV preview, bulk barcode) was a RAW `fetch()` with
 * `credentials: 'include'` and nothing else: no `X-CSRF-Token`, no `Authorization` fallback. On a
 * same-origin cookie session the CSRF middleware rejects the mutation; on a cross-origin deploy
 * (cookies not shared) there is no bearer either — so the upload 401/403'd in exactly the deploy
 * shapes production runs. `fetchAPI` cannot carry a FormData body because it pins
 * `Content-Type: application/json`, which strips the multipart boundary. This helper is the
 * precedent in importApi.js:36-60 lifted into the shared module so no service re-implements it.
 *
 * Errors are normalised the way fetchAPI normalises them (message / flattened 422 detail / status
 * fallback) and carry `.status`, so callers can branch on 404/409/422 the same way they already do.
 *
 * @param {string} endpoint  path under API_BASE
 * @param {FormData} formData  the multipart body (the browser sets the boundary header itself)
 * @param {{method?: string, headers?: Object}} [options]
 */
export async function fetchAPIMultipart(endpoint, formData, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const method = options.method || 'POST';
  const headers = { ...options.headers };

  if (!headers.Authorization) {
    const storedToken = getStoredToken();
    if (storedToken) {
      headers.Authorization = `Bearer ${storedToken}`;
    }
  }

  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(url, {
    method,
    headers,
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401) {
      clearStoredToken();
    }
    const error = await response.json().catch(() => ({}));
    const err = new Error(
      error.message ||
        flattenErrorDetail(error.detail) ||
        `API Error: ${response.status}`
    );
    err.status = response.status;
    if (error.details && typeof error.details === 'object') err.details = error.details;
    throw err;
  }

  if (response.status === 204) return null;
  return response.json();
}

/**
 * The filename a `Content-Disposition` header names, or null when it names none.
 *
 * Handles the three forms servers emit: bare (`filename=x.csv`, which is what
 * collection_export.py sends), quoted (`filename="x.csv"`), and RFC 5987
 * (`filename*=UTF-8''x%20y.csv`).
 */
export function parseContentDispositionFilename(header) {
  if (!header) return null;
  const extended = /filename\*=(?:[\w-]+'[\w-]*')?([^;]+)/i.exec(header);
  if (extended) {
    const raw = extended[1].trim().replace(/^"|"$/g, '');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const plain = /filename=(?:"([^"]*)"|([^;]+))/i.exec(header);
  if (!plain) return null;
  const name = (plain[1] ?? plain[2]).trim();
  return name || null;
}

/**
 * V-EXPORT-FE: fetch a FILE from the API.
 *
 * ⛔ fetchAPI unconditionally calls `response.json()`. POST /vault/export answers
 * `text/csv` for csv/ebay/clz, so every CSV export threw a JSON parse error inside
 * fetchAPI, and the consumers -- built around a Blob that never arrived -- either
 * closed silently (ExportFilterDialog) or threw (MobileExport). Only the JSON format
 * "worked", and only because the consumer re-stringified the parsed object.
 *
 * Same headers, credentials, CSRF and 401-refresh as fetchAPI; the body is read as a
 * Blob and the server's filename is taken from Content-Disposition.
 *
 * @param {string} endpoint
 * @param {Object} [options] fetch options plus `fallbackFilename`, used when the
 *   response carries no Content-Disposition filename.
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export async function fetchAPIBlob(endpoint, options = {}) {
  const { fallbackFilename = 'download', ...fetchOptions } = options;
  const url = `${API_BASE}${endpoint}`;
  const method = fetchOptions.method || 'GET';
  const headers = buildRequestHeaders(endpoint, method, fetchOptions.headers);

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401 && !fetchOptions._retry && (await attemptSilentRefresh())) {
      return fetchAPIBlob(endpoint, { ...options, _retry: true });
    }
    throw await responseToError(response);
  }

  const blob = await response.blob();
  const filename =
    parseContentDispositionFilename(response.headers.get('content-disposition')) || fallbackFilename;
  return { blob, filename };
}

/**
 * Comic API
 * v2.0.0: search() method removed (DEP-SEARCH-001)
 */
export const comicsAPI = {
  /**
   * Get detailed info for a specific issue by ID
   */
  getIssue: async (issueId) => {
    return fetchAPI(`/comics/issue/${issueId}`);
  },

  /**
   * Search for series
   */
  searchSeries: async ({ name, publisher, year, page = 1 }) => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    if (publisher) params.append('publisher', publisher);
    if (year) params.append('year', year);
    params.append('page', page);

    return fetchAPI(`/comics/series?${params.toString()}`);
  },

  /**
   * Get publishers list
   */
  getPublishers: async (page = 1) => {
    return fetchAPI(`/comics/publishers?page=${page}`);
  },

  /**
   * Search characters
   */
  searchCharacters: async (name, page = 1) => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    params.append('page', page);

    return fetchAPI(`/comics/characters?${params.toString()}`);
  },

  /**
   * Search creators
   */
  searchCreators: async (name, page = 1) => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    params.append('page', page);

    return fetchAPI(`/comics/creators?${params.toString()}`);
  },
};

/**
 * Products API (local inventory)
 * FE-PERF-004: Added pagination support
 */
export const productsAPI = {
  /**
   * Get all products with optional pagination and filters
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.per_page - Items per page (default: 100, max: 100)
   * @param {string} options.category - Filter by category
   * @param {string} options.sort - Sort order
   * @param {AbortSignal} options.signal - AbortController signal
   */
  getAll: async ({ page = 1, per_page = 100, category, sort, signal } = {}) => {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('per_page', per_page);
    if (category && category !== 'all') params.append('category', category);
    if (sort) params.append('sort', sort);

    const options = {};
    if (signal) options.signal = signal;

    return fetchAPI(`/products?${params.toString()}`, options);
  },

  getById: async (id) => {
    return fetchAPI(`/products/${id}`);
  },

  search: async (query, { page = 1, per_page = 100 } = {}) => {
    const params = new URLSearchParams();
    params.append('search', query);
    params.append('page', page);
    params.append('per_page', per_page);
    return fetchAPI(`/products?${params.toString()}`);
  },
};

/**
 * Auth API
 *
 * P1-5: Updated for cookie-based auth
 * - Tokens are now set as HttpOnly cookies by the server
 * - We still receive tokens in response for backwards compatibility
 * - Frontend stores csrf_token for sending with mutations
 */
export const authAPI = {
  login: async (email, password) => {
    const result = await fetchAPI('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    // P1-5: Server sets HttpOnly cookies automatically
    // Cross-origin fallback: Also store token in localStorage for header-based auth
    if (result?.access_token) {
      setStoredToken(result.access_token);
    }
    return result;
  },

  // USER-CON-001: Enhanced registration with extra profile fields
  // MAESTRO:CONACCESS P7: extraFields.captcha_token is forwarded when the
  // signup form's CAPTCHA gate is enabled.
  register: async (name, email, password, extraFields = {}) => {
    const result = await fetchAPI('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        name,
        email,
        password,
        // USER-CON-001: Pass enhanced registration fields
        first_name: extraFields.first_name,
        last_name: extraFields.last_name,
        birth_date: extraFields.birth_date,
        accept_terms: extraFields.accept_terms,
        accept_privacy: extraFields.accept_privacy,
        // MAESTRO:CONACCESS P7: optional CAPTCHA solution token.
        captcha_token: extraFields.captcha_token,
      }),
    });
    // P1-5: Server sets HttpOnly cookies automatically
    // Cross-origin fallback: Also store token in localStorage for header-based auth
    if (result?.access_token) {
      setStoredToken(result.access_token);
    }
    return result;
  },

  logout: async () => {
    // P1-5: New endpoint that clears HttpOnly cookies
    // Also clear stored token for cross-origin auth
    clearStoredToken();
    return fetchAPI('/auth/logout', {
      method: 'POST',
    });
  },

  /**
   * Logout from all devices
   * Revokes all tokens issued before now for this user
   */
  logoutAll: async () => {
    return fetchAPI('/auth/logout-all', {
      method: 'POST',
    });
  },

  /**
   * Get current user
   * P1-5: No longer needs token parameter - uses cookie
   * Still accepts token for backwards compatibility
   */
  me: async (token = null) => {
    const options = {};
    // Support legacy header-based auth if token provided
    if (token) {
      options.headers = {
        Authorization: `Bearer ${token}`,
      };
    }
    return fetchAPI('/auth/me', options);
  },

  /**
   * Refresh tokens
   * P1-5: Uses refresh token from cookie, falls back to body
   */
  refresh: async (refreshToken = null) => {
    const options = {
      method: 'POST',
    };
    if (refreshToken) {
      options.body = JSON.stringify({ refresh_token: refreshToken });
    }
    return fetchAPI('/auth/refresh', options);
  },

  // ============================================================
  // Password Management
  // ============================================================

  /**
   * Request password reset email
   * Always returns success to prevent email enumeration.
   * BR-GAP-008: a 429 response from rate-limiting still resolves to the canonical success shape
   * (do NOT surface the rate-limit signal to the visitor — it leaks "this email is being checked").
   */
  requestPasswordReset: async (email) => {
    try {
      return await fetchAPI('/auth/password-reset/request', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    } catch (err) {
      if (err && err.status === 429) {
        return { message: 'If an account with that email exists, a reset link has been sent' };
      }
      throw err;
    }
  },

  /**
   * Confirm password reset with token
   */
  confirmPasswordReset: async (token, newPassword) => {
    return fetchAPI('/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  },

  /**
   * BR-GAP-017: introspect a reset token so the frontend can display the masked email + TTL
   * BEFORE the visitor types a new password (confirms the link belongs to them).
   * Returns { masked_email, ttl_seconds } on 200, null on 400 (anti-enum parity).
   */
  requestPasswordResetIntrospect: async (token) => {
    try {
      return await fetchAPI(`/auth/password-reset/introspect?token=${encodeURIComponent(token)}`, {
        method: 'GET',
      });
    } catch (err) {
      if (err && (err.status === 400 || err.status === 429)) {
        return null;
      }
      throw err;
    }
  },

  /**
   * Change password (requires current password)
   */
  changePassword: async (currentPassword, newPassword) => {
    return fetchAPI('/auth/password/change', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
  },

  /**
   * Get password policy requirements
   */
  getPasswordRequirements: async () => {
    return fetchAPI('/auth/password/requirements');
  },

  /**
   * Check password strength without storing
   */
  checkPasswordStrength: async (password) => {
    return fetchAPI('/auth/password/check-strength', {
      method: 'POST',
      body: JSON.stringify(password),
    });
  },

  // ============================================================
  // Email Verification
  // ============================================================

  /**
   * Request email verification (resend verification email)
   */
  requestEmailVerification: async () => {
    return fetchAPI('/auth/email/request-verification', {
      method: 'POST',
    });
  },

  /**
   * Verify email with token
   */
  verifyEmail: async (token) => {
    return fetchAPI('/auth/email/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  // ============================================================
  // Session Management
  // ============================================================

  /**
   * List active sessions for current user
   */
  getSessions: async () => {
    return fetchAPI('/auth/sessions');
  },

  /**
   * Revoke a specific session
   */
  revokeSession: async (sessionId) => {
    return fetchAPI(`/auth/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Cart API
 *
 * P1-5: Updated for cookie-based auth - no longer needs token parameter
 * Still accepts token for backwards compatibility with mobile/API clients
 */
// ⛔ cartAPI WAS REMOVED HERE -- the client for a server-side cart that nothing used.
//
// It wrapped GET /cart, POST /cart/items, PATCH|DELETE /cart/items/{id} and DELETE /cart. All
// of those routes are gone (see the backend change), and cartAPI had ZERO component references:
// no screen imported it and no literal /cart fetch existed anywhere else in src/.
//
// ⚠ THE REAL CART IS CartContext (localStorage); checkout posts ITS items in the request body.
// Two carts existed and only one was ever wired to a button. Keeping a client for the dead one
// is how the next person wires a feature to the wrong half.

/**
 * Health check
 */
export const healthAPI = {
  check: async () => {
    return fetchAPI('/health');
  },
};

/**
 * Checkout API
 *
 * P1-5: Updated for cookie-based auth
 */
export const checkoutAPI = {
  getConfig: async () => {
    return fetchAPI('/checkout/config');
  },

  // ⛔⛔ S11b. `shipping_address` IS REQUIRED BY THE SERVER AND IT IS REQUIRED HERE.
  // This call used to send `{ items }` alone, which is the entire reason an order could
  // be created with nowhere to send it. It is a positional third argument rather than an
  // option bag so that omitting it is a 422 from the server, loudly, rather than an
  // undefined key that serialises away silently.
  createPaymentIntent: async (token, items, shippingAddress) => {
    const options = {
      method: 'POST',
      body: JSON.stringify({ items, shipping_address: shippingAddress }),
    };
    if (token) {
      options.headers = { Authorization: `Bearer ${token}` };
    }
    return fetchAPI('/checkout/create-payment-intent', options);
  },

  confirmOrder: async (token, paymentIntentId, items) => {
    const options = {
      method: 'POST',
      body: JSON.stringify({ payment_intent_id: paymentIntentId, items }),
    };
    if (token) {
      options.headers = { Authorization: `Bearer ${token}` };
    }
    return fetchAPI('/checkout/confirm-order', options);
  },

  /**
   * Cancel a stock reservation and restore stock
   * Called when user cancels checkout
   */
  cancelReservation: async (paymentIntentId) => {
    return fetchAPI('/checkout/cancel-reservation', {
      method: 'POST',
      // ⛔ WAS JSON.stringify(paymentIntentId) -- which serialises the BARE STRING
      // as `"pi_123"`, not `{"payment_intent_id":"pi_123"}`. Even with the route
      // correctly bound to a body model this would still 422, on shape instead of
      // on binding. Both halves had to change.
      body: JSON.stringify({ payment_intent_id: paymentIntentId }),
    });
  },
};

/**
 * Contact API
 * IMPL-001: About & Contact Page
 */
export const contactAPI = {
  /**
   * Submit contact form
   * @param {Object} formData - { name, email, subject, message }
   * @returns {Promise<{ message: string, reference_id: string }>}
   */
  submit: async (formData) => {
    return fetchAPI('/contact', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  },
};

/**
 * Shipping API
 * UPS Shipping Integration v1.28.0
 */
export const shippingAPI = {
  // Address management
  createAddress: async (addressData) => {
    return fetchAPI('/shipping/addresses', {
      method: 'POST',
      body: JSON.stringify(addressData),
    });
  },

  getAddresses: async (type = null) => {
    const params = type ? `?address_type=${type}` : '';
    return fetchAPI(`/shipping/addresses${params}`);
  },

  getAddress: async (addressId) => {
    return fetchAPI(`/shipping/addresses/${addressId}`);
  },

  validateAddress: async (addressId) => {
    return fetchAPI(`/shipping/addresses/${addressId}/validate`, {
      method: 'POST',
    });
  },

  deleteAddress: async (addressId) => {
    return fetchAPI(`/shipping/addresses/${addressId}`, {
      method: 'DELETE',
    });
  },

  // Carrier management
  getEnabledCarriers: async () => {
    return fetchAPI('/shipping/carriers');
  },

  // Rate quoting
  getRates: async (destinationAddressId, orderId = null, packages = null, carrierCode = null) => {
    const body = {
      destination_address_id: destinationAddressId,
    };
    if (orderId) body.order_id = orderId;
    if (packages) body.packages = packages;
    if (carrierCode) body.carrier_code = carrierCode;

    return fetchAPI('/shipping/rates', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  getMultiCarrierRates: async (destinationAddressId, orderId = null, packages = null, carrierFilter = null) => {
    const body = {
      destination_address_id: destinationAddressId,
    };
    if (orderId) body.order_id = orderId;
    if (packages) body.packages = packages;
    if (carrierFilter) body.carrier_filter = carrierFilter;

    return fetchAPI('/shipping/rates/multi', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  selectRate: async (quoteId) => {
    return fetchAPI('/shipping/rates/select', {
      method: 'POST',
      body: JSON.stringify({ quote_id: quoteId }),
    });
  },

  // Shipment management
  createShipment: async (shipmentData) => {
    return fetchAPI('/shipping/shipments', {
      method: 'POST',
      body: JSON.stringify(shipmentData),
    });
  },

  getShipments: async (orderId = null, status = null, page = 1, pageSize = 20) => {
    const params = new URLSearchParams();
    if (orderId) params.append('order_id', orderId);
    if (status) params.append('status', status);
    params.append('page', page);
    params.append('page_size', pageSize);

    return fetchAPI(`/shipping/shipments?${params.toString()}`);
  },

  getShipment: async (shipmentId) => {
    return fetchAPI(`/shipping/shipments/${shipmentId}`);
  },

  getLabel: async (shipmentId) => {
    return fetchAPI(`/shipping/shipments/${shipmentId}/label`);
  },

  getTracking: async (shipmentId, refresh = false) => {
    return fetchAPI(`/shipping/shipments/${shipmentId}/tracking?refresh=${refresh}`);
  },

  voidShipment: async (shipmentId) => {
    return fetchAPI(`/shipping/shipments/${shipmentId}/void`, {
      method: 'POST',
    });
  },

  // Public tracking
  trackByNumber: async (trackingNumber) => {
    return fetchAPI(`/shipping/track/${trackingNumber}`);
  },
};


/**
 * Customer Refunds API
 * BCW Refund Request Module v1.0.0
 */
export const refundsAPI = {
  /**
   * Check if a product/order item is eligible for refund
   */
  /**
   * Ask the SERVER whether an item can be refunded. The rule used to be written out in four
   * React components; it now lives once, in BCWRefundService.is_bcw_supply, and this asks it.
   */
  checkEligibility: async (orderId, orderItemId) => {
    return fetchAPI(`/refunds/eligibility/${orderId}/${orderItemId}`);
  },

  /**
   * Every item in one order, in ONE request. The account screens render a badge and a button
   * per item, so asking per item would put a request behind every row.
   */
  checkOrderEligibility: async (orderId) => {
    return fetchAPI(`/refunds/eligibility/${orderId}`);
  },

  /**
   * Submit a refund request
   */
  createRequest: async (orderId, orderItemId, reason, details = null) => {
    return fetchAPI('/refunds', {
      method: 'POST',
      body: JSON.stringify({
        order_id: orderId,
        order_item_id: orderItemId,
        reason,
        details,
      }),
    });
  },

  /**
   * Get customer's refund requests
   */
  getMyRequests: async () => {
    return fetchAPI('/refunds');
  },

  /**
   * Get a specific refund request
   */
  getRequest: async (requestId) => {
    return fetchAPI(`/refunds/${requestId}`);
  },

  /**
   * Cancel a pending refund request
   */
  cancelRequest: async (requestId) => {
    return fetchAPI(`/refunds/${requestId}/cancel`, {
      method: 'POST',
    });
  },
};

/**
 * Customer Orders API
 * BCW Refund Request Module v1.0.0
 */
export const ordersAPI = {
  /**
   * Get customer's orders
   */
  getMyOrders: async (page = 1, perPage = 20) => {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('per_page', perPage);
    return fetchAPI(`/orders?${params.toString()}`);
  },

  /**
   * Get a specific order
   */
  getOrder: async (orderId) => {
    return fetchAPI(`/orders/${orderId}`);
  },
};

/**
 * Match Review Queue API (Admin)
 */
export const matchReviewAPI = {
  /**
   * Get queue items with filters
   */
  getQueue: async (filter) => {
    return fetchAPI('/admin/match-queue', {
      method: 'POST',
      body: JSON.stringify(filter),
    });
  },

  /**
   * Get queue statistics
   */
  getStats: async () => {
    return fetchAPI('/admin/match-queue/stats');
  },

  /**
   * Approve a match
   */
  approve: async (matchId, notes = null) => {
    return fetchAPI(`/admin/match-queue/${matchId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  },

  /**
   * Reject a match
   */
  reject: async (matchId, reason, notes = null) => {
    return fetchAPI(`/admin/match-queue/${matchId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason, notes }),
    });
  },

  /**
   * Skip a match for later
   */
  skip: async (matchId, notes = null) => {
    return fetchAPI(`/admin/match-queue/${matchId}/skip`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  },

  /**
   * Bulk approve matches with score >= 8
   */
  bulkApprove: async (matchIds, notes = null) => {
    return fetchAPI('/admin/match-queue/bulk-approve', {
      method: 'POST',
      body: JSON.stringify({ match_ids: matchIds, notes }),
    });
  },

  /**
   * Bulk reject matches with image cleanup
   */
  bulkReject: async (matchIds, reason, notes = null) => {
    return fetchAPI('/admin/match-queue/bulk-reject', {
      method: 'POST',
      body: JSON.stringify({ match_ids: matchIds, reason, notes }),
    });
  },

  /**
   * Manual link entity to PriceCharting
   */
  manualLink: async (entityType, entityId, pricechartingId, notes = null) => {
    return fetchAPI('/admin/match-queue/manual-link', {
      method: 'POST',
      body: JSON.stringify({
        entity_type: entityType,
        entity_id: entityId,
        pricecharting_id: pricechartingId,
        notes,
      }),
    });
  },

  /**
   * Search PriceCharting for manual linking
   */
  search: async (query, entityType) => {
    return fetchAPI('/admin/match-queue/search', {
      method: 'POST',
      body: JSON.stringify({ query, entity_type: entityType }),
    });
  },
};

/**
 * Bundles API
 * CHARLIE-06: Bundle display and cart integration
 */
export const bundlesAPI = {
  /**
   * Get featured bundles for homepage
   * @param {number} limit - Max bundles to return (default 5, max 10)
   * @param {Object} options - Fetch options (signal for abort)
   */
  getFeatured: async (limit = 5, options = {}) => {
    const params = new URLSearchParams();
    params.append('limit', Math.min(limit, 10));

    const fetchOptions = {};
    if (options.signal) fetchOptions.signal = options.signal;

    return fetchAPI(`/bundles/featured?${params.toString()}`, fetchOptions);
  },

  /**
   * Get all active bundles with pagination
   * @param {Object} options - Query options
   */
  getAll: async ({ page = 1, per_page = 20, category, sort, signal } = {}) => {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('per_page', per_page);
    if (category) params.append('category', category);
    if (sort) params.append('sort', sort);

    const fetchOptions = {};
    if (signal) fetchOptions.signal = signal;

    // ⛔ THE TRAILING SLASH IS LOAD-BEARING. The router declares @router.get("/"), and
    // Starlette does NOT redirect here -- measured against the app: GET /api/bundles?page=1
    // returns 404, GET /api/bundles/?page=1 returns 200. A browse page whose list call 404s
    // is a slower way of having no page at all.
    return fetchAPI(`/bundles/?${params.toString()}`, fetchOptions);
  },

  /**
   * Get bundle by slug
   * @param {string} slug - Bundle slug
   */
  getBySlug: async (slug) => {
    return fetchAPI(`/bundles/${slug}`);
  },

  /**
   * Get bundle by ID
   * @param {number} id - Bundle ID
   */
  getById: async (id) => {
    return fetchAPI(`/bundles/id/${id}`);
  },

  // ⛔ addToCart WAS REMOVED HERE. It POSTed /cart/bundle -- a route that has never existed
  // (measured 404; the real one was /cart/items) -- and it had no caller anywhere in src/.
  // Even corrected it would have written a CartItem row, and CartItem is gone because nothing
  // read it. A bundle is purchased through the CLIENT cart.
};

// Feature 3: Email Intelligence Platform — Phase 2
export const emailPreferencesAPI = {
  getPreferences: () => fetchAPI('/email/preferences'),
  updatePreferences: (data) => fetchAPI('/email/preferences', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  getUnsubscribePage: (token) => fetchAPI(`/email/unsubscribe/${token}`),
  submitUnsubscribe: (token, data) => fetchAPI(`/email/unsubscribe/${token}`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getEmailHistory: (page = 1, perPage = 20) => fetchAPI(`/email/history?page=${page}&per_page=${perPage}`),
};

// Re-export middleware API for convenience
export { middlewareAPI, isMiddlewareConfigured } from './middlewareApi.js';

export default {
  comics: comicsAPI,
  products: productsAPI,
  auth: authAPI,
  checkout: checkoutAPI,
  health: healthAPI,
  contact: contactAPI,
  shipping: shippingAPI,
  matchReview: matchReviewAPI,
  refunds: refundsAPI,
  orders: ordersAPI,  bundles: bundlesAPI,
};
